import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Button, Card, Col, Drawer, Empty, Modal, Progress, Row, Spin, Table, Tag,
  Tooltip, message,
} from "antd";
import { DownloadOutlined, ReloadOutlined } from "@ant-design/icons";

import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import { controller as loginController } from "../../Login/controller";
import PlanPicker, { PURPLE } from "../Subscription/PlanPicker";
import { openPlanModal } from "../Subscription/planModal";
import { useOfficeFeatures } from "../officeFeatures";
import { money } from "../Subscription/money";
import {
  INCLUDED, UNLIMITED, invoiceRow, meterLabel, meterPercent, outcomeAlert, planChangeCopy,
  remaining,
} from "./billingDisplay";
import {
  confirmCancellation, confirmTierChange, downloadInvoicePdf, getInvoices,
  getSubscriptionStatus, getUsageSummary, previewCancellation, previewTierChange,
} from "./controller";
import "../CRM/crm.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;

/**
 * Settings → Billing. What the practice is on, what they have used, what they
 * have been charged, and how to change or stop it.
 *
 * Before this page the only billing surface in the product was
 * Membership/Subscriptions.js — a 2022 mockup of invented invoices that the
 * usage-overage modal sent real customers to. A practice could not see their own
 * plan, usage or receipts anywhere.
 *
 * Two things the page must not decide for itself:
 *  - whether a category of work is METERED (the server says; see usage_summary),
 *  - what a plan change costs (the server quotes it; confirm is a second call).
 */

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—";

function Panel({ title, extra, children }) {
  return (
    <Card
      title={<span style={{ fontWeight: 600 }}>{title}</span>}
      extra={extra}
      style={{ marginBottom: 16, borderRadius: 8 }}
      bodyStyle={{ paddingTop: 16 }}
    >
      {children}
    </Card>
  );
}

function MeterRow({ row, metered }) {
  const pct = meterPercent(row, metered);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
        <span>{row.label}</span>
        <span style={{ color: "#666" }}>
          {metered ? row.count : `${row.count} — ${INCLUDED}`}
        </span>
      </div>
      {pct !== null ? (
        <Progress percent={pct} showInfo={false} strokeColor={PURPLE} size="small" />
      ) : null}
    </div>
  );
}

const STATE_TAG = {
  subscribed: ["green", "Active"],
  exempt: ["blue", "Complimentary"],
  trial: ["gold", "Free trial"],
  open: ["default", "No plan"],
  blocked: ["red", "Needs a plan"],
};

function officeName(practicePlans, id) {
  const row = ((practicePlans && practicePlans.offices) || []).find((o) => String(o.office_id) === String(id));
  return row ? row.office_name : "";
}

// `preview`: a super admin sees the Choose buttons the practice sees; the plans
// open as a preview (MultiOfficeCheckout). Change/Cancel stay the practice's own.
function officeColumns({ onChoose, onChange, onCancel, preview = false }) {
  return [
    { title: "Office", dataIndex: "office_name" },
    {
      title: "Plan",
      key: "plan",
      render: (_t, o) => (o.plan && o.plan.tier ? o.plan.tier.title : <span style={{ color: "#999" }}>—</span>),
    },
    {
      title: "Status",
      key: "state",
      width: 140,
      render: (_t, o) => {
        const pending = o.plan && o.plan.status === "pending";
        const [color, label] = pending ? ["orange", "Payment pending"] : (STATE_TAG[o.state] || ["default", o.state]);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Renews",
      key: "renews",
      width: 130,
      render: (_t, o) => (o.plan && o.plan.status === "active" ? fmtDate(o.plan.next_billing) : "—"),
    },
    {
      title: "",
      key: "actions",
      width: 200,
      align: "right",
      render: (_t, o) => {
        if (!o.can_purchase && !preview) return null;
        if (o.plan && o.plan.status === "active") {
          if (!o.can_purchase) return null;
          return (
            <>
              <Button size="small" onClick={() => onChange(o.office_id)}>Change</Button>
              <Button size="small" type="link" danger onClick={() => onCancel(o.office_id)}>Cancel</Button>
            </>
          );
        }
        if (o.state === "exempt") return null;
        return (
          <Button size="small" type="primary" style={{ background: PURPLE, borderColor: PURPLE }}
                  onClick={() => onChoose(o.office_id)}>
            Choose a plan
          </Button>
        );
      },
    },
  ];
}

export default function BillingPage() {
  const officeId = useMemo(() => localStorage.getItem("selectedOffice") || "", []);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [usage, setUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [planDrawer, setPlanDrawer] = useState(false);
  // The office a plan change or cancellation acts on — the selected office, or a
  // row in "Your offices" for a practice with several.
  const [targetOffice, setTargetOffice] = useState(null);
  const [practicePlans, setPracticePlans] = useState(null);
  const [busy, setBusy] = useState(false);
  // The backend refuses billing to anyone who is not the practice's own admin
  // (FTC personnel included, even though they can work the office's insurance
  // data). A 403 here is that answer, not a failure — say so instead of
  // rendering an empty plan card.
  const [forbidden, setForbidden] = useState(false);
  const preview = !!useOfficeFeatures(officeId || undefined).isSuperAdmin;

  const load = useCallback(async () => {
    if (!officeId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const [s, u, i, pp] = await Promise.all([
      getSubscriptionStatus(officeId),
      getUsageSummary(officeId),
      getInvoices(officeId),
      loginController.getPracticePlans(officeId).catch(() => null),
    ]);
    if (pp && pp.status < 250) setPracticePlans(pp);
    if (s.status === 403 || u.status === 403) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    if (ok(s)) setStatus(s.json);
    if (ok(u)) setUsage(u.json);
    if (ok(i)) setInvoices(((i.json || {}).results || []).map(invoiceRow).filter(Boolean));
    setLoading(false);
  }, [officeId]);

  useEffect(() => {
    load();
  }, [load]);

  // --- plan change -------------------------------------------------------------
  const changePlan = useCallback(
    async (tierId) => {
      const forOffice = targetOffice || officeId;
      setBusy(true);
      const preview = await previewTierChange(forOffice, tierId);
      setBusy(false);
      if (!ok(preview)) {
        message.error((preview.json && preview.json.detail) || "Could not price that change.");
        return;
      }
      const quote = preview.json || {};
      if (quote.status === "noop") {
        message.info("You're already on that plan.");
        return;
      }
      const copy = planChangeCopy(quote);
      if (!copy) return;

      Modal.confirm({
        title: copy.title,
        width: 520,
        icon: null,
        okText: copy.okText,
        cancelText: "Not now",
        okButtonProps: { style: { background: PURPLE, borderColor: PURPLE } },
        content: (
          <div style={{ fontSize: 13 }}>
            {copy.lines.map((line, idx) => (
              <div key={idx} style={{ marginTop: idx ? 6 : 10, color: "#444" }}>
                {line}
              </div>
            ))}
            {copy.total ? (
              <div style={{ marginTop: 14, fontSize: 16, fontWeight: 600 }}>
                Charged today: {copy.total}
              </div>
            ) : null}
          </div>
        ),
        onOk: async () => {
          const res = await confirmTierChange(forOffice, tierId);
          const body = res.json || {};
          if (body.status === "changed") {
            message.success("Your plan has been changed.");
          } else if (body.status === "scheduled") {
            message.success("The change is scheduled for your renewal date.");
          } else if (body.status === "declined") {
            message.error(body.detail || "The card was declined. Your current plan is unchanged.");
          } else {
            message.error(body.detail || "Could not change the plan.");
          }
          setPlanDrawer(false);
          load();
        },
      });
    },
    [officeId, targetOffice, load]
  );

  const openChange = (forOffice) => {
    setTargetOffice(String(forOffice || officeId));
    setPlanDrawer(true);
  };

  // --- cancellation ------------------------------------------------------------
  const cancelPlan = useCallback(async (forOfficeArg) => {
    const forOffice = String(forOfficeArg || officeId);
    const preview = await previewCancellation(forOffice);
    if (!ok(preview)) {
      message.error((preview.json && preview.json.detail) || "Could not load cancellation details.");
      return;
    }
    const p = preview.json || {};
    const penalty = Number(p.penalty || 0);
    Modal.confirm({
      title: "Cancel your subscription",
      width: 520,
      icon: null,
      okText: penalty > 0 ? `Cancel and pay $${money(p.penalty)}` : "Cancel subscription",
      okButtonProps: { danger: true },
      cancelText: "Keep my plan",
      content: (
        <div style={{ fontSize: 13, color: "#444" }}>
          {penalty > 0 ? (
            <p>
              Your plan is a commitment, so cancelling now charges an early-termination fee of{" "}
              <strong>${money(p.penalty)}</strong>.
            </p>
          ) : (
            <p>Your plan is paid through its current term. Nothing further is charged.</p>
          )}
          <p>Your team keeps access until the end of the period you have paid for.</p>
        </div>
      ),
      onOk: async () => {
        const res = await confirmCancellation(forOffice);
        const body = res.json || {};
        if (body.status === "cancelled" || body.status === "already_cancelled") {
          message.success("Your subscription has been cancelled.");
        } else if (body.status === "charge_failed") {
          message.error("We could not take the cancellation fee, so nothing was cancelled.");
        } else {
          message.error(body.detail || "Could not cancel the subscription.");
        }
        load();
      },
    });
  }, [officeId, load]);

  const onDownload = useCallback(
    async (row) => {
      const res = await downloadInvoicePdf(officeId, row.id);
      if (!res.ok) message.error("Could not produce that receipt. Please try again.");
    },
    [officeId]
  );

  if (!officeId) {
    return (
      <DashboardLayout>
        <Alert type="warning" showIcon message="Choose an office first to see its billing." />
      </DashboardLayout>
    );
  }

  if (forbidden) {
    return (
      <DashboardLayout>
        <Empty
          style={{ marginTop: 64 }}
          description="Billing isn't available for your role — it belongs to the practice's owner."
        />
      </DashboardLayout>
    );
  }

  const plan = (usage && usage.plan) || null;
  // {kind, text} unwrapped to a string in billingDisplay — never render the pair.
  const planAlert = outcomeAlert(status && status.state);
  const agentMetered = usage ? usage.agent.metered : true;
  const handedOver = usage ? usage.ftc.handed_over_from_agent : 0;

  const columns = [
    { title: "Date", dataIndex: "date", render: (d) => fmtDate(d), width: 130 },
    {
      title: "Description",
      dataIndex: "description",
      render: (text, row) => (
        <span>
          {text}
          {row.discount ? (
            <div style={{ color: "#2e7d32", fontSize: 12 }}>{row.discount}</div>
          ) : null}
          {row.failed ? (
            <Tag color="red" style={{ marginLeft: 8 }}>{row.statusLabel}</Tag>
          ) : null}
        </span>
      ),
    },
    {
      title: "Tax",
      key: "tax",
      width: 150,
      render: (_t, row) => (row.taxLines.length ? (
        <span>
          {row.taxLines.map((t, i) => (
            <div key={i}>
              {t.amount} <span style={{ color: "#999", fontSize: 12 }}>{t.label}</span>
              {row.taxEstimated && i === row.taxLines.length - 1 ? (
                <Tooltip title="Calculated at the 13% HST that applied at the time — this charge predates us recording the split.">
                  <span style={{ color: "#999", marginLeft: 4 }}>*</span>
                </Tooltip>
              ) : null}
            </div>
          ))}
        </span>
      ) : <span style={{ color: "#999" }}>—</span>),
    },
    { title: "Total", dataIndex: "amount", width: 120 },
    {
      title: "",
      key: "pdf",
      width: 60,
      render: (_t, row) => (
        <Button type="link" icon={<DownloadOutlined />} onClick={() => onDownload(row)} />
      ),
    },
  ];

  return (
    <DashboardLayout>
      <div style={{ padding: "8px 4px 40px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0 }}>Billing</h2>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </div>

        {!loading && practicePlans && (practicePlans.offices || []).length > 1 ? (
          <Panel
            title="Your offices"
            extra={
              (practicePlans.offices || []).some((o) => (o.can_purchase || preview)
                && !(o.plan && o.plan.status === "active") && o.state !== "exempt") ? (
                <Button type="primary" style={{ background: PURPLE, borderColor: PURPLE }}
                        onClick={() => openPlanModal({ officeId, reason: "billing" })}>
                  Choose plans
                </Button>
              ) : null
            }
          >
            <Table
              rowKey="office_id"
              size="small"
              pagination={false}
              dataSource={practicePlans.offices}
              columns={officeColumns({ onChoose: (id) => openPlanModal({ officeId: id, reason: "billing" }),
                                       onChange: openChange, onCancel: cancelPlan, preview })}
            />
          </Panel>
        ) : null}

        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}><Spin /></div>
        ) : (
          <Row gutter={16}>
            <Col xs={24} lg={14}>
              <Panel
                title="Your plan"
                extra={
                  <Button type="primary" style={{ background: PURPLE, borderColor: PURPLE }}
                          onClick={() => (plan ? openChange(officeId)
                            : openPlanModal({ officeId, reason: "billing" }))}
                          loading={busy}>
                    {plan ? "Change plan" : "Choose a plan"}
                  </Button>
                }
              >
                {plan ? (
                  <>
                    <div style={{ fontSize: 20, fontWeight: 600 }}>{plan.title}</div>
                    {status && status.next_billing ? (
                      <div style={{ color: "#666", marginTop: 4 }}>
                        Renews {fmtDate(status.next_billing)}
                      </div>
                    ) : null}
                    {planAlert ? (
                      <Alert style={{ marginTop: 12 }} type={planAlert.type} showIcon
                             message={planAlert.text} />
                    ) : null}
                    <Button danger type="link" style={{ paddingLeft: 0, marginTop: 12 }}
                            onClick={() => cancelPlan(officeId)}>
                      Cancel subscription
                    </Button>
                  </>
                ) : (
                  <Empty description="No active plan for this office." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Panel>

              <Panel title="Receipts">
                {invoices.length ? (
                  <Table rowKey="id" size="small" columns={columns} dataSource={invoices}
                         pagination={{ pageSize: 10, hideOnSinglePage: true }} />
                ) : (
                  <Empty description="No charges yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Panel>
            </Col>

            <Col xs={24} lg={10}>
              <Panel title="This billing period">
                {usage ? (
                  <>
                    <div style={{ color: "#888", fontSize: 12, marginBottom: 14 }}>
                      {fmtDate(usage.cycle.start)} – {fmtDate(usage.cycle.end)}
                    </div>

                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Team requests</div>
                    {usage.ftc.monthly.limit !== null ? (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                          <span>Used this period</span>
                          <span>{meterLabel({ count: usage.ftc.monthly.used, limit: usage.ftc.monthly.limit })}</span>
                        </div>
                        <Progress
                          percent={meterPercent({ count: usage.ftc.monthly.used, limit: usage.ftc.monthly.limit })}
                          showInfo={false} strokeColor={PURPLE} size="small" />
                        <div style={{ fontSize: 12, color: "#888" }}>
                          {remaining(usage.ftc.monthly)} left
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: "#666", marginBottom: 10 }}>{UNLIMITED}</div>
                    )}
                    {usage.ftc.by_action.map((row) => (
                      <MeterRow key={row.action} row={row} metered />
                    ))}
                    {handedOver > 0 ? (
                      <Alert
                        style={{ marginTop: 10 }}
                        type="info"
                        showIcon
                        message={
                          `${handedOver} of these started as an automatic check that could not be ` +
                          `completed, and were finished by our team.`
                        }
                      />
                    ) : null}

                    <div style={{ fontWeight: 600, margin: "18px 0 8px" }}>Automatic checks</div>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
                      {agentMetered
                        ? "These count toward your free trial."
                        : "Included with your plan — these don't count toward your limits."}
                    </div>
                    {usage.agent.by_action.map((row) => (
                      <MeterRow key={row.action} row={row} metered={agentMetered} />
                    ))}
                  </>
                ) : (
                  <Empty description="No usage recorded." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Panel>
            </Col>
          </Row>
        )}

        <Drawer title={`Change the plan${officeName(practicePlans, targetOffice) ? " for " + officeName(practicePlans, targetOffice) : ""}`}
                width={920} open={planDrawer}
                onClose={() => setPlanDrawer(false)} destroyOnClose>
          <PlanPicker officeId={targetOffice || officeId} onSubscribe={changePlan}
                      submitting={busy} submitLabel="Choose this plan" />
        </Drawer>
      </div>
    </DashboardLayout>
  );
}
